import { useState } from 'react';
import { Row, Card, Col, Typography, Space, Switch, Tag } from 'antd';
import { Channels } from '../../../common/channelsEnum';
import { getChannelReadableName } from '../../../../utils/readable';
import {
  DeliveryOptions,
  InappWebDeliveryOptions,
  Notification
} from '../../../../interfaces/backend/Notification';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  SendOutlined,
  SettingOutlined,
  StopOutlined
} from '@ant-design/icons';
import ChannelOptionsModal from './ChannelOptionsModal';
import {
  EmailDeliveryOptionsEnum,
  InappWebDeliveryOptionsEnum
} from '../../NotificationTabsContainer';

export interface OverviewProps {
  notification: Notification;
  updateNotification: (notification: Notification) => void;
}

const Overview: React.FC<OverviewProps> = ({
  notification,
  updateNotification
}: OverviewProps): JSX.Element => {
  const [savingChannels, setSavingChannels] = useState<Channels>();
  const [channelOptionsModalOpen, setChannelOptionsModalOpen] =
    useState<Channels | undefined>();

  const changeTitle = async (title: string): Promise<void> => {
    if (title === '') return;

    const newNotification: Notification = {
      ...notification,
      title
    };
    await updateNotification(newNotification);
  };

  const changeActiveChannel = async (
    channel: Channels,
    isActive: boolean
  ): Promise<void> => {
    setSavingChannels(channel);
    const newActiveChannelsList = [...notification.channels];
    if (isActive) {
      newActiveChannelsList.push(channel);
    } else {
      for (let i = 0; i < newActiveChannelsList.length; i++) {
        if (newActiveChannelsList[i] === channel) {
          newActiveChannelsList.splice(i, 1);
          i--;
        }
      }
    }

    const newNotification: Notification = {
      ...notification,
      channels: newActiveChannelsList
    };
    await updateNotification(newNotification);
    setSavingChannels(undefined);
  };

  // const saveNotification = () => {
  //   const mergedNotification = deepMerge(
  //     notification as unknown as Record<string, unknown>,
  //     updatedNotification
  //   ) as unknown as Notification;

  //   mergedNotification.channels = updatedNotification.channels;

  //   updateNotification(mergedNotification);
  //   setShowModal(false);
  // };

  const generateTagsForEmailDeliveryOptions = (n: Notification) => {
    const def = n.options?.EMAIL?.defaultDeliveryOption;
    const tags: React.ReactNode[] = [];
    const options = Object.values(EmailDeliveryOptionsEnum).sort((a, b) => {
      if (a === def) return -1;
      else return 1;
    });
    const emailOptions = n.options?.EMAIL as DeliveryOptions;

    options.forEach((option: EmailDeliveryOptionsEnum, i) => {
      const isDefault = option === def;
      const isEnabled = emailOptions[option]?.enabled;
      const color = isDefault ? 'green' : 'default';
      const defaultText = isDefault ? ' (default)' : '';

      if (option === 'instant' && isEnabled) {
        tags.push(
          <Tag key="instant" color={color} icon={<SendOutlined />}>
            Instant{defaultText}
          </Tag>
        );
      }

      if (option === 'hourly' && isEnabled) {
        tags.push(
          <Tag key="hourly" color={color} icon={<ClockCircleOutlined />}>
            {isDefault ? 'Batching Hourly' : 'Hourly'}
            {defaultText}
          </Tag>
        );
      }

      if (option === 'daily' && isEnabled) {
        tags.push(
          <Tag key="daily" color={color} icon={<CalendarOutlined />}>
            Daily {emailOptions.daily?.hour}
            {defaultText}
          </Tag>
        );
      }

      if (option === 'weekly' && isEnabled) {
        tags.push(
          <Tag key="weekly" color={color} icon={<CalendarOutlined />}>
            Weekly {emailOptions.weekly?.day} {emailOptions.weekly?.hour}
            {defaultText}
          </Tag>
        );
      }

      if (option === 'monthly' && isEnabled) {
        tags.push(
          <Tag key="monthly" color={color} icon={<CalendarOutlined />}>
            Monthly {emailOptions.monthly?.date}{' '}
          </Tag>
        );
      }

      if (option === 'off') {
        if (isEnabled) {
          tags.push(
            <Tag key="off" icon={<StopOutlined />} color={color}>
              {isDefault ? 'Unsubbed by default' : 'Allow Unsub'}
            </Tag>
          );
        } else {
          tags.push(
            <Tag key="off" icon={<StopOutlined />} color="red">
              Disallow Unsub
            </Tag>
          );
        }
      }
    });

    // no batching
    if (
      !emailOptions.hourly?.enabled &&
      !emailOptions.daily?.enabled &&
      !emailOptions.weekly?.enabled &&
      !emailOptions.monthly?.enabled
    ) {
      tags.push(
        <Tag key="batch" icon={<CalendarOutlined />} color="default">
          No Batching
        </Tag>
      );
    }

    return tags;
  };
  const generateTagsForInAppWebDeliveryOptions = (n: Notification) => {
    const def =
      n.options?.INAPP_WEB?.defaultDeliveryOption ??
      InappWebDeliveryOptionsEnum.INSTANT;
    const tags: React.ReactNode[] = [];
    const options = Object.values(InappWebDeliveryOptionsEnum).sort((a, b) => {
      if (a === def) return -1;
      else return 1;
    });
    const inappWebOptions = n.options?.INAPP_WEB as InappWebDeliveryOptions;

    options.forEach((option, i) => {
      const isDefault = option === def;
      const isEnabled = inappWebOptions[option]?.enabled;
      const color = isDefault ? 'green' : 'default';
      const defaultText = isDefault ? ' (default)' : '';

      if (option === 'instant' && isEnabled) {
        if (inappWebOptions.instant?.batching) {
          tags.push(
            <Tag key="instant" color={color} icon={<SendOutlined />}>
              Instant & batch{defaultText}
            </Tag>
          );
        } else {
          tags.push(
            <Tag key="instant" color={color} icon={<SendOutlined />}>
              Instant{defaultText}
            </Tag>
          );
        }
      }

      if (option === 'off') {
        if (isEnabled) {
          tags.push(
            <Tag key="off" icon={<StopOutlined />} color={color}>
              {isDefault ? 'Unsubbed by default' : 'Allow Unsub'}
            </Tag>
          );
        } else {
          tags.push(
            <Tag key="off" icon={<StopOutlined />} color="red">
              Disallow Unsub
            </Tag>
          );
        }
      }
    });

    // no batching
    if (!inappWebOptions.instant?.batching) {
      tags.push(
        <Tag key="batch" icon={<CalendarOutlined />} color="default">
          No Batching
        </Tag>
      );
    }

    return tags;
  };
  return (
    <>
      <Row>
        <Col>
          <Card>
            <Row justify="space-between">
              <Col>
                <Row>
                  <Typography.Text type="secondary" strong>
                    Title:
                  </Typography.Text>
                </Row>
                <Row>
                  <Typography.Text
                    strong
                    editable={{
                      onChange: changeTitle
                    }}
                  >
                    {notification.title}
                  </Typography.Text>
                </Row>
                <Row>
                  <Typography.Text type="secondary">
                    Human-readable name for the notification
                  </Typography.Text>
                </Row>

                <Row style={{ marginTop: '24px' }}>
                  <Typography.Text type="secondary" strong>
                    Notification ID:
                  </Typography.Text>
                </Row>
                <Row>
                  <Typography.Text strong copyable>
                    {notification.notificationId}
                  </Typography.Text>
                </Row>
                <Row>
                  <Typography.Text type="secondary">
                    Used in code
                  </Typography.Text>
                </Row>
              </Col>
            </Row>
          </Card>

          <Card title="Channels" style={{ marginTop: '12px' }}>
            <Space size="middle" direction="vertical">
              {Object.values(Channels).map((channel) => (
                <Row key={channel}>
                  <Col>
                    <Switch
                      key={channel}
                      loading={savingChannels === channel}
                      disabled={savingChannels !== undefined}
                      checked={notification.channels.includes(channel)}
                      onChange={(state): void => {
                        if (state === false || channel !== Channels.EMAIL)
                          changeActiveChannel(channel, state);
                        else setChannelOptionsModalOpen(Channels.EMAIL);
                      }}
                      style={{ marginRight: '4px' }}
                    />
                    <Typography.Text style={{ marginRight: 12 }}>
                      {getChannelReadableName(channel)}
                    </Typography.Text>

                    {channel === Channels.EMAIL && (
                      <>
                        <Typography.Link
                          type="secondary"
                          onClick={() => {
                            setChannelOptionsModalOpen(Channels.EMAIL);
                          }}
                        >
                          {generateTagsForEmailDeliveryOptions(notification)}
                          <SettingOutlined /> edit
                        </Typography.Link>
                      </>
                    )}
                    {channel === Channels.INAPP_WEB && (
                      <>
                        <Typography.Link
                          type="secondary"
                          onClick={() => {
                            setChannelOptionsModalOpen(Channels.INAPP_WEB);
                          }}
                        >
                          {generateTagsForInAppWebDeliveryOptions(notification)}
                          <SettingOutlined /> edit
                        </Typography.Link>
                      </>
                    )}
                  </Col>
                </Row>
              ))}
            </Space>

            <ChannelOptionsModal
              channel={channelOptionsModalOpen as Channels}
              notification={notification}
              updateNotification={updateNotification}
              isOpen={
                channelOptionsModalOpen === Channels.EMAIL ||
                channelOptionsModalOpen === Channels.INAPP_WEB
              }
              close={() => setChannelOptionsModalOpen(undefined)}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};
export default Overview;
